import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CardV2 as Card, SegmentedControl, TimeFrameSelect } from '@feathr/components';
import { useTimeFrameSelect } from '@feathr/extender/hooks';
import { ECardCollapseKeys, ENamespaces } from '@feathr/hooks';

import AudienceEngagementCharts from './AudienceEngagementCharts';
import AudienceEngagementStats from './AudienceEngagementStats/AudienceEngagementStats';

import * as styles from './AudienceEngagement.css';

export type TView = 'community' | 'connections' | 'conversions';

function AudienceEngagement(): JSX.Element {
  const { t } = useTranslation();
  const [view, setView] = useState<TView>('community');

  const { options, timeFrame, timeFrameValue, handleChangeTimeFrame } = useTimeFrameSelect();

  function handleChangeView(view: string): void {
    setView(view as TView);
  }

  const data = [
    {
      label: t('Community'),
      value: 'community',
    },
    {
      label: t('Connections'),
      value: 'connections',
    },
    {
      label: t('Conversions'),
      value: 'conversions',
    },
  ];

  return (
    <Card contentClassName={styles.root} width={'full'}>
      <Card.Header
        isCollapsible={true}
        persist={{
          key: ECardCollapseKeys.AudienceEngagement,
          namespace: ENamespaces.AccountDashboard,
        }}
        title={t('Audience Engagement')}
        width={'full'}
      >
        <TimeFrameSelect onChange={handleChangeTimeFrame} options={options} value={timeFrame} />
      </Card.Header>
      <Card.Content
        contentClassName={styles.content}
        name={'audience-engagement-card-content'}
        padding={'relaxed'}
      >
        <AudienceEngagementStats timeFrameValue={timeFrameValue} />
        <div key={'controls'}>
          <SegmentedControl
            data={data}
            name={'audience-segmented-control'}
            onChange={handleChangeView}
            value={view}
          />
        </div>
        <AudienceEngagementCharts timeFrameValue={timeFrameValue} view={view} />
      </Card.Content>
    </Card>
  );
}

export default observer(AudienceEngagement);
